<div class="page-wrap" [class.is-embedded]="embedded">
  <div class="pinned" *ngIf="!embedded">
    <button nbButton size="small" ghost type="button" (click)="back()">
      <nb-icon icon="arrow-back-outline"></nb-icon> {{ 'crm.col.opportunity' | translate }}
    </button>
    <button nbButton size="small" ghost type="button" *ngIf="fromBoard" (click)="backToBoard()">
      <nb-icon icon="grid-outline"></nb-icon> {{ 'crm.submissions' | translate }}
    </button>
  </div>

  <div *ngIf="errorMessage" class="error-banner">{{ errorMessage }}</div>

  <ng-container *ngIf="submission as s">
    <div class="sub-header">
      <div class="title-row">
        <nb-user [name]="consultantName()"
                 [picture]="Globals.logo(s.consultant)"
                 size="large"></nb-user>
        <div class="header-actions">
          <button nbButton size="tiny" status="danger" ghost type="button"
                  *ngIf="canWrite && canRemove" [disabled]="saving" (click)="removeSubmission()">
            <nb-icon icon="trash-2-outline"></nb-icon> {{ 'crm.remove' | translate }}
          </button>
          <button nbButton size="tiny" ghost *ngIf="embedded" (click)="back()">
            <nb-icon icon="close-outline"></nb-icon>
          </button>
        </div>
      </div>
      <div class="sub-meta">
        <span class="status-badge" [class]="'sub-is-' + s.status">{{ ('crm.sub.' + s.status) | translate }}</span>
        <a class="meta-item meta-link" *ngIf="s.opportunity?.title"
           [routerLink]="['/opportunities', s.Opportunity_id]">{{ 'crm.col.opportunity' | translate }}: {{ s.opportunity?.title }}</a>
        <a class="meta-item meta-link" *ngIf="s.opportunity?.end_client?.name"
           [routerLink]="['/clients', s.opportunity?.end_client?.id]">{{ s.opportunity?.end_client?.name }}</a>
      </div>
      <!-- Boxed and collapsed by default, mirroring opportunity-detail.component.html:
           a vacancy-mirrored description arrives scraped verbatim and can run to
           hundreds of lines, which pushed Status/Documents/Motivation below the fold. -->
      <section class="opp-description-block" *ngIf="descriptionOf(s.opportunity)">
        <header class="desc-head">
          <span class="desc-label">{{ 'description' | translate }}</span>
          <button nbButton ghost size="tiny" type="button"
                  [attr.aria-expanded]="descExpanded"
                  (click)="descExpanded = !descExpanded">
            {{ (descExpanded ? 'showless' : 'showmore') | translate }}
          </button>
        </header>
        <p class="opp-description" [class.is-collapsed]="!descExpanded">{{ descriptionOf(s.opportunity) }}</p>
      </section>
    </div>

    <!-- Two columns on wide screens: left = status + CV + convert, right = motivation. -->
    <div class="detail-grid">
      <div class="detail-col detail-col--side">
        <!-- Status -->
        <section class="block" *ngIf="canWrite">
          <h2 class="section-title"><nb-icon icon="flag-outline"></nb-icon>{{ 'status' | translate }}</h2>
          <div class="row">
            <nb-select [placeholder]="'status' | translate" [(ngModel)]="selectedStatus" [disabled]="isConverted" (selectedChange)="saveStatus()">
              <nb-option *ngFor="let st of pipeline" [value]="st">{{ ('crm.sub.' + st) | translate }}</nb-option>
            </nb-select>
            <nb-select [placeholder]="'crm.loss_reason' | translate" [(ngModel)]="lossReason" *ngIf="isLost" (selectedChange)="saveStatus()">
              <nb-option *ngFor="let r of lossReasons" [value]="r">{{ ('crm.loss.' + r) | translate }}</nb-option>
            </nb-select>
          </div>
          <div class="loss-note-row">
            <label class="loss-note-label">{{ 'crm.rate_offered' | translate }}</label>
            <input nbInput fullWidth type="number" min="0" step="0.5"
                   [placeholder]="s.opportunity?.rate_target ? ('crm.rate_offered_max' | translate:{ value: s.opportunity?.rate_target }) : ''"
                   [(ngModel)]="rateOffered"
                   [disabled]="isConverted"
                   (blur)="saveRate()">
          </div>
          <div class="loss-note-row" *ngIf="isLost">
            <label class="loss-note-label">{{ 'crm.loss_note_label' | translate }}</label>
            <textarea nbInput fullWidth rows="2"
                      [placeholder]="'crm.loss_note_placeholder' | translate"
                      [(ngModel)]="lossNote"
                      (blur)="saveStatus()"></textarea>
          </div>
          <div class="loss-note-row" *ngIf="isInterviewScheduled">
            <label class="loss-note-label">{{ 'crm.interview.label' | translate }}</label>
            <input nbInput fullWidth
                   [placeholder]="'enterdate' | translate"
                   [nbDatepicker]="interviewPicker"
                   [(ngModel)]="interviewAt"
                   (ngModelChange)="saveStatus()">
            <nb-datepicker #interviewPicker></nb-datepicker>
          </div>
        </section>

        <!-- Documents -->
        <section class="block">
          <div class="section-header-row">
            <h2 class="section-title"><nb-icon icon="file-text-outline"></nb-icon>{{ 'crm.documents' | translate }}</h2>
            <span class="docs-chip"
                  [class.docs-chip--complete]="docsComplete"
                  [class.docs-chip--missing]="!docsComplete">
              <nb-icon [icon]="docsComplete ? 'checkmark-circle-2-outline' : 'alert-circle-outline'"></nb-icon>
              <ng-container *ngIf="docsComplete">{{ 'crm.docs_complete' | translate }}</ng-container>
              <ng-container *ngIf="!docsComplete && !cvReady">{{ 'crm.cv_missing' | translate }}</ng-container>
              <ng-container *ngIf="!docsComplete && cvReady && !letterReady">{{ 'crm.letter_missing' | translate }}</ng-container>
            </span>
          </div>

          <div class="doc-cards">
            <!-- CV card -->
            <div class="doc-card">
              <div class="doc-card__head">
                <nb-icon icon="file-text-outline" class="doc-card__icon"></nb-icon>
                <span class="doc-card__label">CV</span>
                <span class="doc-status-chip"
                      [class.doc-status-chip--ready]="cvReady"
                      [class.doc-status-chip--missing]="!cvReady">
                  <nb-icon [icon]="cvReady ? 'checkmark-outline' : 'close-outline'"></nb-icon>
                  {{ (cvReady ? 'crm.doc_ready' : 'crm.doc_missing') | translate }}
                </span>
              </div>
              <div class="doc-card__actions">
                <button nbButton size="tiny" status="primary" ghost type="button"
                        *ngIf="canGenerate" (click)="generate()">
                  <nb-icon icon="flash-outline"></nb-icon> {{ 'crm.generate' | translate }}
                </button>
                <span class="meta-item" *ngIf="canWrite && !canGenerate">{{ 'crm.generate_admin_only' | translate }}</span>
                <button nbButton size="tiny" ghost type="button"
                        [disabled]="!cvReady" (click)="openCv()"
                        *ngIf="s.GeneratedCv_id || s.cv">
                  <nb-icon icon="eye-outline"></nb-icon> {{ 'crm.open_cv' | translate }}
                </button>
                <button nbButton size="tiny" ghost type="button"
                        [disabled]="!s.application_package?.cv_pdf_gcs_key"
                        (click)="openPackageCv()"
                        *ngIf="s.application_package">
                  <nb-icon icon="download-outline"></nb-icon> {{ 'crm.package_cv' | translate }}
                </button>
              </div>
              <div class="doc-card__attach">
                <nb-select [placeholder]="'crm.attach_cv_placeholder' | translate" [(ngModel)]="selectedCvId" *ngIf="canWrite && cvOptions.length" size="tiny" fullWidth [disabled]="isConverted" (selectedChange)="saveCv()">
                  <nb-option *ngFor="let cv of cvOptions" [value]="cv.id">{{ cvLabel(cv) }}</nb-option>
                </nb-select>
              </div>
              <span class="meta-item" *ngIf="s.GeneratedCv_id && !s.application_package">{{ 'crm.attached_cv' | translate:{id: s.GeneratedCv_id} }}</span>
            </div>

            <!-- Cover letter card -->
            <div class="doc-card">
              <div class="doc-card__head">
                <nb-icon icon="email-outline" class="doc-card__icon"></nb-icon>
                <span class="doc-card__label">{{ 'crm.cover_letter' | translate }}</span>
                <span class="doc-status-chip"
                      [class.doc-status-chip--ready]="letterReady"
                      [class.doc-status-chip--missing]="!letterReady">
                  <nb-icon [icon]="letterReady ? 'checkmark-outline' : 'close-outline'"></nb-icon>
                  {{ (letterReady ? 'crm.doc_ready' : 'crm.doc_missing') | translate }}
                </span>
              </div>
              <div class="doc-card__actions">
                <button nbButton size="tiny" status="primary" ghost type="button"
                        *ngIf="canGenerate" (click)="generate()">
                  <nb-icon icon="flash-outline"></nb-icon> {{ 'crm.generate' | translate }}
                </button>
                <span class="meta-item" *ngIf="canWrite && !canGenerate">{{ 'crm.generate_admin_only' | translate }}</span>
                <button nbButton size="tiny" ghost type="button"
                        [disabled]="!s.application_package?.letter_pdf_gcs_key"
                        (click)="openLetter()"
                        *ngIf="s.application_package">
                  <nb-icon icon="download-outline"></nb-icon> {{ 'crm.open' | translate }}
                </button>
              </div>
              <div class="doc-card__attach">
                <nb-select [placeholder]="'crm.attach_letter_placeholder' | translate" [(ngModel)]="selectedPackageId" *ngIf="canWrite && packageOptions.length" size="tiny" fullWidth [disabled]="isConverted" (selectedChange)="saveLetterPackage()">
                  <nb-option *ngFor="let pkg of packageOptions" [value]="pkg.id">{{ packageLabel(pkg) }}</nb-option>
                </nb-select>
              </div>
            </div>
          </div>
        </section>

        <!-- Convert to project -->
        <section class="block" *ngIf="isWon">
          <h2 class="section-title"><nb-icon icon="briefcase-outline"></nb-icon>{{ 'crm.convert.section' | translate }}</h2>
          <div class="row" *ngIf="canWrite && !isConverted">
            <button nbButton size="small" status="success" type="button"
                    [disabled]="saving" (click)="convert()">{{ 'crm.convert.to_project' | translate }}</button>
          </div>
          <span class="meta-item" *ngIf="isConverted">
            {{ 'crm.convert.already' | translate:{id: s.converted_Project_id} }}
          </span>
        </section>

      </div>

      <div class="detail-col detail-col--main">
        <!-- Motivation -->
        <section class="block">
          <h2 class="section-title"><nb-icon icon="edit-2-outline"></nb-icon><span class="hint-tip" tabindex="0" [nbTooltip]="'crm.motivation_hint' | translate" [attr.aria-description]="'crm.motivation_hint' | translate">{{ 'crm.motivation' | translate }}</span></h2>
          <textarea nbInput fullWidth rows="10" *ngIf="canWrite"
                    [placeholder]="'crm.motivation_placeholder' | translate"
                    [(ngModel)]="motivation"
                    [disabled]="isConverted"
                    (blur)="saveMotivation()"></textarea>
          <p class="meta-item" *ngIf="!canWrite">{{ submission?.motivation_text || ('crm.no_motivation' | translate) }}</p>
        </section>

        <!-- Activity timeline (newest-first from API) -->
        <section class="block">
          <h2 class="section-title"><nb-icon icon="activity-outline"></nb-icon>{{ 'crm.activity' | translate }}</h2>
          <bst-crm-activity-log *ngIf="canWrite" [saving]="savingActivity" [contacts]="contacts" (add)="addActivity($event)"></bst-crm-activity-log>
          <bst-crm-timeline *ngIf="activities.length" [activities]="activities" [canEdit]="canWrite"
                             (edit)="editActivity($event)" (delete)="deleteActivity($event)"></bst-crm-timeline>
          <div class="crm-empty" *ngIf="!isLoading && !activities.length">{{ 'crm.no_activity' | translate }}</div>
        </section>
      </div>
    </div>
  </ng-container>
</div>
